import { PieChartLabelsPlugin } from '@/utils/PieChartLabelsPlugin.ts';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  type ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
//import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { accountingFormatNumber } from '@/utils';

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend, ArcElement, PieChartLabelsPlugin);

interface BarChartProps {
  dataLabels?: string[];
  colors?: boolean;
  datasets?: Array<number | null | undefined>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  formatValuesAsCurrency?: boolean;
  options?: ChartOptions<'bar'>;
}

const DEFAULT_COLORS = ['#D9B64E', '#2B6CB0', '#0FCA98', '#F27141', '#008080', '#00adad', '#d600d6', '#ffde24', '#bc8e52', '#570000'];

const formatAxisValue = (value: number | string, asCurrency: boolean) => {
  const num = typeof value === 'string' ? Number(value) : value;

  if (!Number.isFinite(num)) {
    return '';
  }

  return asCurrency ? accountingFormatNumber({ num }) : num.toLocaleString();
};

const normalizeChartPoints = (dataLabels: string[] | undefined, datasets: Array<number | null | undefined> | undefined) => {
  const labels: string[] = [];
  const data: number[] = [];
  const backgroundColor: string[] = [];

  const safeLabels = Array.isArray(dataLabels) ? dataLabels : [];
  const safeDatasets = Array.isArray(datasets) ? datasets : [];

  safeLabels.forEach((label, index) => {
    const rawValue = safeDatasets[index];
    const value = typeof rawValue === 'string' ? Number(rawValue) : rawValue;

    if (value == null || !Number.isFinite(value) || value === 0) {
      return;
    }

    labels.push(label?.trim() ? label : 'Unknown');
    data.push(value);
    backgroundColor.push(DEFAULT_COLORS[index % DEFAULT_COLORS.length]);
  });

  return { labels, data, backgroundColor };
};

const BarChart = ({
  dataLabels = [],
  colors,
  datasets = [],
  xAxisLabel,
  yAxisLabel,
  formatValuesAsCurrency = false,
  options: optionsOverride,
}: BarChartProps) => {
  const chartPoints = useMemo(() => normalizeChartPoints(dataLabels, datasets), [dataLabels, datasets]);

  const chartData = useMemo<ChartData<'bar'>>(
    () => ({
      labels: chartPoints.labels,
      datasets: [
        {
          label: yAxisLabel ?? '',
          data: chartPoints.data,
          backgroundColor: colors ? chartPoints.backgroundColor : '#0FCA98',
        },
      ],
    }),
    [chartPoints, colors, yAxisLabel]
  );

  const chartOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      ...optionsOverride,
      plugins: {
        legend: {
          position: 'bottom',
          align: 'start',
          display: false,
        },
        tooltip: {
          callbacks: {
            title: (items) => items[0]?.label ?? '',
            label: (context) => formatAxisValue(context.parsed.y ?? 0, formatValuesAsCurrency),
          },
        },
        ...optionsOverride?.plugins,
      },
      scales: {
        x: {
          ...optionsOverride?.scales?.x,
          title: xAxisLabel
            ? {
                display: true,
                text: xAxisLabel,
                font: { weight: 'bold' },
                padding: 10,
              }
            : optionsOverride?.scales?.x?.title,
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 0,
            ...optionsOverride?.scales?.x?.ticks,
          },
          grid: {
            display: false,
            ...optionsOverride?.scales?.x?.grid,
          },
        },
        y: {
          ...optionsOverride?.scales?.y,
          beginAtZero: true,
          title: yAxisLabel
            ? {
                display: true,
                text: yAxisLabel,
                font: { weight: 'bold' },
                padding: 10,
              }
            : optionsOverride?.scales?.y?.title,
          ticks: {
            ...optionsOverride?.scales?.y?.ticks,
            padding: 10,
            callback: (value) => formatAxisValue(value, formatValuesAsCurrency),
          },
          grid: {
            color: '#eee',
            ...optionsOverride?.scales?.y?.grid,
          },
        },
      },
    }),
    [formatValuesAsCurrency, optionsOverride, xAxisLabel, yAxisLabel]
  );

  if (chartPoints.labels.length === 0) {
    return (
      <div className='relative flex h-72 w-full items-center justify-center text-sm text-neutralColor-normal'>No chart data available</div>
    );
  }

  return (
    <div className='relative h-96 w-full'>
      <Bar data={chartData} options={chartOptions} />
    </div>
  );
};

export default BarChart;
